import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {
  ReProductionFormData,
  ReProductionType,
  ToastType,
  ProductionStatus,
} from '../ReProductionUtils';

import './SelectProductions.scss';
import ArrCheckbox from '../../../common/arr-checkbox/ArrCheckbox';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {GetPriorProductionListRequestParam} from '../../../../api/types/reproduction.type';
import {
  useReProductions,
  useSavePriorProductionToReProduce,
} from '../../../../hooks/productions/useReProductions';
import {useDispatch} from 'react-redux';
import {setReproductionIsFieldsChanged} from '../../../../redux/slice/reproductionSlice';

interface SelectProductionsProps {
  securityGroupId: number;
  productionSetId: number;
  reProductionId: number;
  reProductionType: ReProductionType | null;
  formData: ReProductionFormData;
  onFormDataChange: (updates: Partial<ReProductionFormData>) => void;
  getStatus: (isShowToast: boolean, status: ToastType, message: string) => void;
  goNext: () => void;
  goBack: () => void;
  onStateChange: (state: {
    saveDisabled: boolean;
    nextDisabled: boolean;
  }) => void;
  productionStatusCode?: string;
}

const SelectProduction = 're-production-select-productions';

const SelectProductions = forwardRef<
  {save: () => void; next: () => void},
  SelectProductionsProps
>(
  (
    {
      securityGroupId,
      productionSetId,
      reProductionId,
      formData,
      onFormDataChange,
      goNext,
      onStateChange,
      getStatus,
      productionStatusCode,
    },
    ref
  ) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const isInProgressOrCompleted =
      productionStatusCode === ProductionStatus.COMPLETED ||
      productionStatusCode === ProductionStatus.INPROGRESS;

    const [selectedId, setSelectedId] = useState<number>(
      formData?.selectedProductionIds?.[0] ?? 0
    );

    const {data: priorProductionsList, isLoading: isPriorProductionsLoading} =
      useReProductions(
        {
          securityGroupId: securityGroupId,
          productionSetId: productionSetId,
          reProductionId: reProductionId,
          pageSize: -1,
        } as GetPriorProductionListRequestParam,

        Boolean(productionSetId) &&
          Number.isFinite(securityGroupId) &&
          securityGroupId > 0
      );

    const productions = useMemo(
      () => priorProductionsList?.priorProductionsDetails ?? [],
      [priorProductionsList]
    );
    useEffect(() => {
      if (productions.length > 0 && selectedId === 0) {
        const selectedItem = productions.find((p) => p.isSelected);

        if (selectedItem) {
          setSelectedId(selectedItem.priorProductionId);
        }
      }
    }, [productions]);
    useEffect(() => {
      const isDisabled = selectedId === 0;
      onStateChange({
        saveDisabled: isInProgressOrCompleted || isDisabled,
        nextDisabled: isInProgressOrCompleted ? false : isDisabled,
      });
    }, [selectedId, onStateChange, isInProgressOrCompleted]);

    useEffect(() => {
      dispatch(setReproductionIsFieldsChanged({isFieldsChanged: false}));
    }, [dispatch]);

    const {mutateAsync: savePriorProduction} =
      useSavePriorProductionToReProduce();

    useImperativeHandle(ref, () => ({
      save: () => {
        const payload = {
          reProductionId: reProductionId,
          securityGroupId: securityGroupId,
          priorProductionId: selectedId,
          isMarkComplete: false,
        };

        void savePriorProduction(payload)
          .then(() => {
            getStatus(
              true,
              ToastType.Success,
              t('reProduction.toastMessages.saveSuccess')
            );
            onFormDataChange({
              selectedProductionIds: [selectedId],
            });
          })
          .catch((err: unknown) => {
            getStatus(
              true,
              ToastType.Error,
              err instanceof Error ? err.message : String(err)
            );
          });
      },
      next: async () => {
        if (isInProgressOrCompleted) {
          goNext();
          return;
        }
        const payload = {
          reProductionId: reProductionId,
          securityGroupId: securityGroupId,
          priorProductionId: selectedId,
          isMarkComplete: true,
        };

        try {
          await savePriorProduction(payload);

          getStatus(
            true,
            ToastType.Success,
            t('reProduction.toastMessages.nextSuccess')
          );
          onFormDataChange({
            selectedProductionIds: [selectedId],
          });

          goNext();
        } catch (err: unknown) {
          getStatus(
            true,
            ToastType.Error,
            err instanceof Error ? err.message : String(err)
          );
        }
      },
    }));
    const handleRowSelect = useCallback((productionId: number) => {
      setSelectedId((prev) => (prev === productionId ? 0 : productionId));
    }, []);

    // Spinner while loading data
    if (isPriorProductionsLoading) {
      return (
        <Box className={`${SelectProduction}__spinner-container`}>
          <ArrLoadingSpinner />
        </Box>
      );
    }

    return (
      <Box id="select-productions-root" className={SelectProduction}>
        <Box
          id="select-productions-header"
          className={`${SelectProduction}__header`}
        >
          <Typography
            id="select-productions-header-title"
            className={`${SelectProduction}__header-title`}
          >
            {t('reProduction.selectProductions.header.title')}
          </Typography>
        </Box>

        <Box
          id="select-productions-body"
          className={`${SelectProduction}__body`}
        >
          <Box
            id="select-productions-description-wrapper"
            className={`${SelectProduction}__description-wrapper`}
          >
            <Typography
              id="select-productions-description-line1"
              className={`${SelectProduction}__description`}
            >
              {t('reProduction.selectProductions.description.line1')}
            </Typography>
            <Typography
              id="select-productions-description-line2"
              className={`${SelectProduction}__description`}
              align="left"
            >
              {t('reProduction.selectProductions.description.line2')}
            </Typography>
          </Box>

          <TableContainer
            id="select-productions-table-container"
            className={`${SelectProduction}__table-container`}
          >
            <Table
              id="select-productions-table"
              stickyHeader
              aria-label={t('reProduction.selectProductions.tableAriaLabel')}
              className={`${SelectProduction}__table`}
            >
              <TableHead id="select-productions-table-head">
                <TableRow
                  id="select-productions-table-head-row"
                  className={`${SelectProduction}__table-head-row`}
                >
                  <TableCell
                    id="select-productions-th-checkbox"
                    className={`${SelectProduction}__th`}
                  ></TableCell>

                  <TableCell
                    id="select-productions-th-name"
                    className={`${SelectProduction}__th`}
                  >
                    {t(
                      'reProduction.selectProductions.columns.priorProductionName'
                    )}
                  </TableCell>

                  <TableCell
                    id="select-productions-th-selected-doc-count"
                    className={`${SelectProduction}__th`}
                  >
                    {t(
                      'reProduction.selectProductions.columns.selectedDocCount'
                    )}
                  </TableCell>

                  <TableCell
                    id="select-productions-th-prior-doc-count"
                    className={`${SelectProduction}__th`}
                  >
                    {t(
                      'reProduction.selectProductions.columns.priorProductionDocCount'
                    )}
                  </TableCell>

                  <TableCell
                    id="select-productions-th-bates-range"
                    className={`${SelectProduction}__th`}
                  >
                    {t(
                      'reProduction.selectProductions.columns.priorProductionBatesRange'
                    )}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody id="select-productions-table-body">
                {productions.map((item) => {
                  const isSelected = selectedId === item.priorProductionId;
                  const batesRange = item.priorProductionBatesRange
                    ? item.priorProductionBatesRange
                    : '—';

                  return (
                    <TableRow
                      id={`select-productions-row-${item.priorProductionId}`}
                      key={item.priorProductionId}
                      hover
                      selected={isSelected}
                      onClick={() =>
                        !isInProgressOrCompleted &&
                        handleRowSelect(item.priorProductionId)
                      }
                      className={`${SelectProduction}__table-row${isSelected ? ` ${SelectProduction}__table-row--selected` : ''}`}
                      data-testid={`production-row-${item.priorProductionId}`}
                      aria-selected={isSelected}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (isInProgressOrCompleted) return;
                        if (e.key === ' ' || e.key === 'Enter') {
                          e.preventDefault();
                          handleRowSelect(item.priorProductionId);
                        }
                      }}
                    >
                      <TableCell
                        id={`select-productions-td-checkbox-${item.priorProductionId}`}
                        padding="checkbox"
                        className={`${SelectProduction}__td ${SelectProduction}__td--checkbox`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ArrCheckbox
                          id={`select-productions-checkbox-${item.priorProductionId}`}
                          checked={isSelected}
                          onChange={() =>
                            !isInProgressOrCompleted &&
                            handleRowSelect(item.priorProductionId)
                          }
                          disabled={isInProgressOrCompleted}
                          data-testid={`checkbox-${item.priorProductionId}`}
                          aria-label={`select-production-${item.priorProductionId}`}
                        />
                      </TableCell>

                      <TableCell
                        id={`select-productions-td-name-${item.priorProductionId}`}
                        className={`${SelectProduction}__td ${SelectProduction}__td--name`}
                      >
                        <Typography
                          id={`select-productions-name-text-${item.priorProductionId}`}
                          className={`${SelectProduction}__production-name`}
                        >
                          {item.priorProductionName}
                        </Typography>
                      </TableCell>

                      <TableCell
                        id={`select-productions-td-selected-docs-${item.priorProductionId}`}
                        className={`${SelectProduction}__td`}
                      >
                        {item.reProductionDocCount ?? '0'}
                      </TableCell>

                      <TableCell
                        id={`select-productions-td-produce-doc-${item.priorProductionId}`}
                        className={`${SelectProduction}__td`}
                      >
                        {item.priorProductionDocCount?.toLocaleString()}
                      </TableCell>

                      <TableCell
                        id={`select-productions-td-bates-${item.priorProductionId}`}
                        className={`${SelectProduction}__td`}
                      >
                        {batesRange}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    );
  }
);

SelectProductions.displayName = 'SelectProductions';
export default SelectProductions;
